import type { CodeLanguage } from '@pebblecode/shared';

import { createContext } from './gen/context.js';
import { generateCpp } from './gen/cpp.js';
import { generateJs } from './gen/js.js';
import { generatePseudo } from './gen/pseudo.js';
import { generatePy } from './gen/py.js';
import type { Program } from './ir.js';
import type { CodeWriter, GeneratedCode } from './writer.js';

export * from './blocks.js';
export * from './ir.js';
export * from './format.js';
export * from './interpreter.js';
export * from './examples.js';
export { parseWorkspace } from './parse.js';
export type { GeneratedCode, LineRange } from './writer.js';
export * from './text/index.js';
export { toWorkspace, type WorkspaceJson } from './toWorkspace.js';

export function generate(program: Program, language: CodeLanguage): GeneratedCode {
  const ctx = createContext(program);
  const generators: Record<CodeLanguage, typeof generateJs> = {
    cpp: generateCpp,
    pseudo: generatePseudo,
    py: generatePy,
    js: generateJs,
  };
  const w: CodeWriter = generators[language](ctx);
  return { language, code: w.toString(), lineMap: w.lineMap, headMap: w.headMap };
}

/** Innermost block whose lines contain `line` (for selecting a block by clicking its code). */
export function blockAtLine(code: GeneratedCode, line: number): string | null {
  let best: string | null = null;
  let bestSize = Infinity;
  for (const [id, [start, end]] of Object.entries(code.headMap)) {
    if (line >= start && line <= end && end - start < bestSize) {
      best = id;
      bestSize = end - start;
    }
  }
  if (best) return best;
  for (const [id, [start, end]] of Object.entries(code.lineMap)) {
    if (line >= start && line <= end && end - start < bestSize) {
      best = id;
      bestSize = end - start;
    }
  }
  return best;
}
